/**
 * A button and modal dialog for flagging a program or evaluation.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {PermissionsInfo} from "@khan/shared-core";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import {FLAG_POST_MUTATION} from "#discussion-package/graphql/flag_post_mutation.ts";
import {userMeetsCommunityRequirements} from "#discussion-package/util.tsx";

import ScratchpadFlagModal from "./scratchpad-flag-modal.tsx";

type ButtonProps = JSX.LibraryManagedAttributes<
    typeof Button,
    React.ComponentProps<typeof Button>
>;

type ButtonKindType = ButtonProps["kind"];
type ButtonSizeType = ButtonProps["size"];

type FlagModalProps = {
    id: string;
    flaggedByUser: boolean;
    flagButton: boolean;
    // A function that will close the modal once called
    closeModal: () => unknown;
    submitFlag: (arg1: string, arg2: string) => void;
    meetsCommunityRequirements?: boolean;
};

class FlagModal extends React.Component<FlagModalProps> {
    render(): React.ReactNode {
        const {meetsCommunityRequirements} = this.props;

        if (!PermissionsInfo.isLoggedIn || !meetsCommunityRequirements) {
            return (
                <OnePaneDialog
                    title={t`Oh noes!`}
                    content={
                        <Body>
                            {!PermissionsInfo.isLoggedIn
                                ? t`You must log in to flag.`
                                : t`To flag, your account must be at least 3 days old, have a verified email address, and have at least 5,000 energy points.`}
                        </Body>
                    }
                    footer={
                        <View>
                            <Button
                                kind="primary"
                                onClick={this.props.closeModal}
                            >
                                {t`Okay`}
                            </Button>
                        </View>
                    }
                    style={styles.loginModal}
                />
            );
        }

        return (
            <ScratchpadFlagModal
                onSelectReason={(...args) => this.props.submitFlag(...args)}
                closeModal={this.props.closeModal}
                isComputerProgramming={false}
            />
        );
    }
}

type FlagControlsProps = {
    id: string;
    flaggedByUser: boolean;
    flagButton: boolean;
} & WithUserProfileProps &
    WithApolloProps;

type FlagControlsState = {
    sendingFlag: boolean;
    justFlagged: boolean;
};

/* Controls shown to non-author, non-moderators. These users can only flag
 * inappropriate content.
 * The actual flagging is taking care of by a generic JS that works on the
 * React as well as the old code.
 */
class FlagControls extends React.Component<
    FlagControlsProps,
    FlagControlsState
> {
    state: FlagControlsState = {
        sendingFlag: false,
        justFlagged: false,
    };

    submitFlag: (flagType: string, flagJustification: string) => void = (
        flagType: string,
        flagJustification: string,
    ) => {
        this.setState({sendingFlag: true});
        this.props.gqlClient
            .mutate(FLAG_POST_MUTATION, {
                variables: {
                    entityKey: this.props.id,
                    flag: flagType,
                    justification: flagJustification,
                },
            })
            .then((response) => {
                this.setState({justFlagged: true, sendingFlag: false});
            })
            .catch((err) => {
                this.setState({sendingFlag: false});
            });
    };

    render(): React.ReactNode {
        const {profile} = this.props;
        const flagged = this.props.flaggedByUser || this.state.justFlagged;
        const buttonLabel = flagged ? t`Flagged` : t`Flag`;
        let buttonKind: ButtonKindType = "tertiary";
        let buttonSize: ButtonSizeType = "small";
        if (this.props.flagButton) {
            buttonKind = "secondary";
            buttonSize = "medium";
        }

        const meetsCommunityRequirements =
            userMeetsCommunityRequirements(profile);

        const flagModalProps: FlagModalProps = {
            id: this.props.id,
            flaggedByUser: this.props.flaggedByUser,
            flagButton: this.props.flagButton,
            closeModal: () => {},
            submitFlag: this.submitFlag,
        };

        return (
            <ModalLauncher
                modal={({closeModal}) => (
                    <FlagModal
                        {...flagModalProps}
                        closeModal={closeModal}
                        submitFlag={this.submitFlag}
                        meetsCommunityRequirements={meetsCommunityRequirements}
                    />
                )}
            >
                {({openModal}) => (
                    <Button
                        kind={buttonKind}
                        size={buttonSize}
                        disabled={flagged}
                        spinner={this.state.sendingFlag}
                        onClick={openModal}
                    >
                        {buttonLabel}
                    </Button>
                )}
            </ModalLauncher>
        );
    }
}

const styles = StyleSheet.create({
    loginModal: {
        maxHeight: 250,
    },
});

type ExportProps = WithoutApollo<
    WithoutUserProfile<
        JSX.LibraryManagedAttributes<
            typeof FlagControls,
            React.ComponentProps<typeof FlagControls>
        >
    >
>;
export default compose(
    withApollo,
    withUserProfile,
)(FlagControls) as React.ComponentType<ExportProps>;
